import {
  TEAM_ADMIN,
  TEAM_OWNER,
  transactionRoles,
  transactionsFilters,
} from 'settings/constants/roles';
import { LISTED_STATUS, transactionStatuses } from 'settings/constants/transactionStatus';

export const TRANSACTION_TABS = {
  Listings: 'Listings',
  Purchases: 'Purchases',
  Dual: 'Dual',
  Closed: 'Closed',
  Cancelled: 'Canceled',
  Expired: 'Expired',
  Archive: 'Archive',
};

type preparedTransactionDataType = {
  active?: any[];
  closed?: any[];
  cancelled?: any[];
  listings?: any[];
  purchases?: any[];
  dual?: any[];
  archivedTransactions?: {
    closed?: any[];
    cancelled?: any[];
    expired?: any[];
  };
};

export const prepareTransactionData = (
  isThirdParty: boolean,
  dataProps,
): preparedTransactionDataType => {
  if (isThirdParty) return prepareThirdPartyTransactionData(dataProps);
  return prepareOtherRolesTransactionData(dataProps);
};

const prepareThirdPartyTransactionData = (dataProps): preparedTransactionDataType => {
  const {
    transactions: transactionDetails,
    agentRole,
    isSearchApplied,
    transactionFilter,
  } = dataProps;

  const transactions = transactionDetails.map((t) => {
    return {
      ...t,
      isAlertVisible: !!t.TransactionAccess,
    };
  });

  const active = transactions.filter(
    (transaction) =>
      transaction.Status !== transactionStatuses.Closed &&
      transaction.Status !== transactionStatuses.Canceled,
  );
  const closed = transactions.filter(
    (transaction) => transaction.Status === transactionStatuses.Closed,
  );
  const cancelled = transactions.filter(
    (transaction) => transaction.Status === transactionStatuses.Canceled,
  );

  return {
    active,
    closed,
    cancelled,
  };
};

const prepareOtherRolesTransactionData = (dataProps): preparedTransactionDataType => {
  const {
    transactions: transactionDetails,
    agentRole,
    isSearchApplied,
    transactionFilter,
  } = dataProps;

  const transactionsData: any = {
    purchasesTableData: [],
    listingTableData: [],
    dualTableData: [],
    closedTransactionsTableData: [],
    cancelledTransactionsTableData: [],
    expiredTransactionsTableData: [],
    allData: [],
  };

  let transactions = transactionDetails.map((t) => {
    let isAlertVisible = false;
    if (
      ([TEAM_OWNER, TEAM_ADMIN].includes(agentRole) && t.IsTeamTransaction) ||
      t.TransactionAccess
    ) {
      isAlertVisible = true;
    }

    return {
      ...t,
      isAlertVisible,
    };
  });

  for (const transaction of transactions) {
    let keepItem = true;
    const archivedTransaction = [
      transactionStatuses.Closed,
      transactionStatuses.Canceled,
      LISTED_STATUS.Expired,
    ].includes(transaction.Status);

    if (!keepItem) continue;

    switch (transactionFilter) {
      case transactionsFilters.myTransactions:
        keepItem = transaction.IsMyTransaction;
        break;

      case transactionsFilters.teamTransactions:
        keepItem = transaction.IsTeamTransaction && !transaction.IsMyTransaction;
        break;

      case transactionsFilters.externalTransactions:
        keepItem = !transaction.IsTeamTransaction && !transaction.IsMyTransaction;
        break;
    }

    if (!keepItem) continue;

    const roles = transaction.RepresentingRoles || [];

    const addPurchaseData =
      roles.includes(transactionRoles.BUYER) && !roles.includes(transactionRoles.SELLER);

    if (addPurchaseData && !archivedTransaction) {
      transactionsData.purchasesTableData.push(transaction);
    }

    const addListingData =
      !roles.includes(transactionRoles.BUYER) && roles.includes(transactionRoles.SELLER);

    if (addListingData && !archivedTransaction) {
      transactionsData.listingTableData.push(transaction);
    }

    const addDualData =
      roles.includes(transactionRoles.BUYER) && roles.includes(transactionRoles.SELLER);

    if (addDualData && !archivedTransaction) {
      transactionsData.dualTableData.push(transaction);
    }

    const addClosedData = transaction.Status === transactionStatuses.Closed;

    if (addClosedData) {
      transactionsData.closedTransactionsTableData.push(transaction);
    }

    const addCanceledData = transaction.Status === transactionStatuses.Canceled;

    if (addCanceledData) {
      transactionsData.cancelledTransactionsTableData.push(transaction);
    }

    const addExpiredData = transaction.Status === LISTED_STATUS.Expired;

    if (addExpiredData) {
      transactionsData.expiredTransactionsTableData.push(transaction);
    }
  }

  const filteredListingTableData = isSearchApplied
    ? transactionsData.listingTableData.filter(
        (transaction) => transaction.Status !== transactionStatuses.Canceled,
      )
    : transactionsData.listingTableData;

  const filteredPurchasesTableData = isSearchApplied
    ? transactionsData.purchasesTableData.filter(
        (transaction) => transaction.Status !== transactionStatuses.Canceled,
      )
    : transactionsData.purchasesTableData;

  const filteredDualTableData = isSearchApplied
    ? transactionsData.dualTableData.filter(
        (transaction) => transaction.Status !== transactionStatuses.Canceled,
      )
    : transactionsData.dualTableData;

  const filteredCanceledTableData = isSearchApplied
    ? transactions.filter((transaction) => transaction.Status === transactionStatuses.Canceled)
    : transactionsData.cancelledTransactionsTableData;

  const filteredClosedTableData = isSearchApplied
    ? transactions.filter((transaction) => transaction.Status === transactionStatuses.Closed)
    : transactionsData.closedTransactionsTableData;

  const filteredExpiredTableData = isSearchApplied
    ? transactions.filter((transaction) => transaction.Status === LISTED_STATUS.Expired)
    : transactionsData.expiredTransactionsTableData;

  return {
    listings: filteredListingTableData,
    purchases: filteredPurchasesTableData,
    dual: filteredDualTableData,
    archivedTransactions: {
      closed: filteredClosedTableData,
      cancelled: filteredCanceledTableData,
      expired: filteredExpiredTableData,
    },
  };
};
